import React, { useMemo } from "react";
import HealthCard from "./HealthCard";
import { averageMapValues, formatNumber } from "../utils";

const sparklineFromMap = (map, points = 8) => {
  const values = Object.values(map || {})
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n) && Number.isFinite(n))
    .sort((a, b) => a - b);
  if (!values.length) return [];
  if (values.length <= points) return values;
  // Sample evenly across sorted fleet values for a stable sparkline.
  const out = [];
  for (let i = 0; i < points; i += 1) {
    const idx = Math.round((i / (points - 1)) * (values.length - 1));
    out.push(values[idx]);
  }
  return out;
};

/**
 * fmd/14 = current costPerKm average
 * fmd/26 = historical / previous costPerKm average (last 6–12 months)
 * data: { current: { costPerKm, costPerDay }, previous: { ... } }
 * Negative delta → downward red graph; positive → upward green graph.
 */
const CostPerKm = ({ translate, data, loading }) => {
  const { currentAvg, previousAvg, deltaPct, sparkValues, vehicleCount } =
    useMemo(() => {
      const currentMap = data?.current?.costPerKm || data?.costPerKm || {};
      const previousMap =
        data?.previous?.costPerKm || data?.old?.costPerKm || {};
      const current = averageMapValues(currentMap);
      const previous = averageMapValues(previousMap);
      const delta =
        previous > 0
          ? ((current - previous) / previous) * 100
          : current > 0
            ? 100
            : 0;
      return {
        currentAvg: current,
        previousAvg: previous,
        deltaPct: delta,
        sparkValues: sparklineFromMap(currentMap),
        vehicleCount: Object.keys(currentMap).length,
      };
    }, [data]);

  const isNegative = deltaPct < 0;
  const stroke = isNegative ? "#5cb85c" : "#e0484c";

  const points = useMemo(() => {
    if (!sparkValues.length) return "";
    // Positive → ascending (up), negative → descending (down)
    const series = isNegative ? [...sparkValues].reverse() : sparkValues;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const span = max - min || 1;
    return series
      .map((value, i, arr) => {
        const x = (i / Math.max(arr.length - 1, 1)) * 240;
        const y = 48 - ((value - min) / span) * 40;
        return `${x},${y}`;
      })
      .join(" ");
  }, [sparkValues, isNegative]);

  const baselineY = useMemo(() => {
    if (!sparkValues.length || previousAvg <= 0) return 24;
    const series = isNegative ? [...sparkValues].reverse() : sparkValues;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const span = max - min || 1;
    const clamped = Math.min(max, Math.max(min, previousAvg));
    return 48 - ((clamped - min) / span) * 40;
  }, [sparkValues, previousAvg, isNegative]);

  return (
    <HealthCard
      className="fh-span-3"
      title={translate("costPerKm")}
      subtitle={
        translate("costPerKmSub") || "Current vs average (last 3-6 months)"
      }
      badge={`${isNegative ? "−" : "+"}${Math.abs(deltaPct).toFixed(0)}%`}
      badgeStyle={
        !isNegative
          ? { background: "#fdeaea", color: "#e0484c" }
          : { background: "#e8f5e8", color: "#3f9c40" }
      }
      loading={loading}
    >
      <div className="fh-kpi-visual fh-kpi-visual--stack">
        <div className="fh-kpi">
          {formatNumber(currentAvg, 2)} <small>{translate("aed")} / km</small>
        </div>
        <div
          className={`fh-delta ${isNegative ? "down" : "up"}`}
          style={{ color: stroke }}
        >
          {isNegative ? "▼" : "▲"}{" "}
          {translate("vsHistoricalAvg") || "vs historical avg"} (
          {isNegative ? "−" : "+"}
          {Math.abs(deltaPct).toFixed(1)}%)
        </div>
        <svg
          viewBox="0 0 240 54"
          width="100%"
          height="36"
          preserveAspectRatio="none"
          className="fh-kpi-spark"
        >
          <line
            x1="0"
            y1={baselineY}
            x2="240"
            y2={baselineY}
            stroke="#cfd6dc"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          {points ? (
            <polyline
              points={points}
              fill="none"
              stroke={stroke}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null}
        </svg>
      </div>
      <div className="fh-kpi-meta">
        <div className="fh-metaline">
          <span>
            {translate("historicalAvgCostPerKm") ||
              "Avg cost / km (last 3–6 months)"}
          </span>
          <b>
            {formatNumber(previousAvg, 2)} {translate("aed")} / km
          </b>
        </div>
        <div className="fh-metaline">
          <span>{translate("vehiclesAffected") || "Vehicles"}</span>
          <b>{formatNumber(vehicleCount)}</b>
        </div>
      </div>
    </HealthCard>
  );
};

export default CostPerKm;
